// Simplified closet component catalog - Shelves, Single Shelf, Drawers, Hanging, Filler, Shoe Shelves, Buffer, Bottom Filler

export const CATALOG_CATEGORIES = {
  SINGLE_SHELF: 'single-shelf',
  SHELVES: 'shelves',
  DRAWERS: 'drawers',
  HANGING: 'hanging',
  SHOE_SHELVES: 'shoe-shelves',
  VERTICAL_FILLER: 'vertical-filler',    // Renamed from FILLER
  HORIZONTAL_FILLER: 'horizontal-filler', // New horizontal filler
  BUFFER: 'buffer',
  MULTI_COLUMN_CONTAINER: 'multi-column-container', // V1: nested column layout block
  HOOK_RACK: 'hook-rack',                 // NEW Feb 2026: wall-mounted coat-hook panel
  TV_NICHE: 'tv-niche',                   // NEW Feb 2026: open recess for TV / media
  PANT_RACK: 'pant-rack',                 // NEW Feb 2026: pull-out trouser rack
  MIRROR: 'mirror',                       // NEW Feb 2026: tall vertical mirror panel
  CORNER: 'corner',                       // NEW Feb 2026: L-shaped corner cabinet spanning 2 walls
  // Legacy - keep for backward compatibility with existing designs
  FILLER: 'filler',
  BOTTOM_FILLER: 'bottom-filler',
};

// Default horizontal filler thickness
export const DEFAULT_HORIZONTAL_FILLER_THICKNESS = 1; // inches

export const HANGING_TYPES = {
  SINGLE: 'single',
  DOUBLE: 'double',
  TRIPLE: 'triple',
};

export const CATALOG = [
  // Single Shelf (NEW - simple single shelf unit, no count logic)
  {
    id: 'single-shelf',
    name: 'Single Shelf',
    category: CATALOG_CATEGORIES.SINGLE_SHELF,
    description: 'Single open shelf unit',
    icon: 'square',
    constraints: {
      minWidth: 12,
      maxWidth: 48,
      minHeight: 8,
      maxHeight: 24,
      minDepth: 12,
      maxDepth: 24,
    },
    defaultProps: {
      width: 24,
      height: 12,
      depth: 24,
    },
  },
  
  // Shelves (multi-level, min count = 2)
  {
    id: 'shelves',
    name: 'Shelves',
    category: CATALOG_CATEGORIES.SHELVES,
    description: 'Multi-level shelf unit (2+ levels)',
    icon: 'shelves',
    constraints: {
      minWidth: 12,
      maxWidth: 48,
      minHeight: 12,
      maxHeight: 96,
      minDepth: 12,
      maxDepth: 24,
    },
    defaultProps: {
      width: 24,
      height: 40, // 4 levels × 10" = 40"
      depth: 24,
      count: 4,
      eachLevelHeight: 10, // inches per shelf level
    },
  },
  
  // Drawers
  {
    id: 'drawers',
    name: 'Drawers',
    category: CATALOG_CATEGORIES.DRAWERS,
    description: 'Drawer stack unit',
    icon: 'drawers',
    constraints: {
      minWidth: 12,
      maxWidth: 36,
      minHeight: 12,
      maxHeight: 60,
      minDepth: 12,
      maxDepth: 24,
    },
    defaultProps: {
      width: 24,
      height: 36,
      depth: 24,
      count: 4,
      eachDrawerHeight: 8, // inches per drawer
    },
  },
  
  // Hanging (Single/Double/Triple - NO count)
  {
    id: 'hanging',
    name: 'Hanging',
    category: CATALOG_CATEGORIES.HANGING,
    description: 'Hanging section with rod(s)',
    icon: 'hanging',
    constraints: {
      minWidth: 18,
      maxWidth: 96,
      minHeight: 36,
      maxHeight: 96,
      minDepth: 12,
      maxDepth: 24,
    },
    defaultProps: {
      width: 30,
      height: 84,
      depth: 24,
      hangingType: HANGING_TYPES.DOUBLE, // single, double, triple
    },
  },
  
  // Shoe Shelves (NEW - with count)
  {
    id: 'shoe-shelves',
    name: 'Shoe Shelves',
    category: CATALOG_CATEGORIES.SHOE_SHELVES,
    description: 'Angled shoe shelf unit',
    icon: 'shoe-shelves',
    constraints: {
      minWidth: 18,
      maxWidth: 48,
      minHeight: 24,
      maxHeight: 72,
      minDepth: 12,
      maxDepth: 18,
    },
    defaultProps: {
      width: 30,
      height: 48,
      depth: 14,
      count: 5,
      eachLevelHeight: 8, // inches per shoe shelf level
    },
  },
  
  // Vertical Filler (renamed from "Filler")
  {
    id: 'vertical-filler',
    name: 'Vertical Filler',
    category: CATALOG_CATEGORIES.VERTICAL_FILLER,
    description: 'Vertical gap filler panel',
    icon: 'filler',
    constraints: {
      minWidth: 0.375,
      maxWidth: 6,
      minHeight: 12,
      maxHeight: 96,
      minDepth: 12,
      maxDepth: 24,
    },
    defaultProps: {
      width: 3,
      height: 84,
      depth: 24,
      count: 1,
    },
  },
  
  // Horizontal Filler (new component type)
  {
    id: 'horizontal-filler',
    name: 'Horizontal Filler',
    category: CATALOG_CATEGORIES.HORIZONTAL_FILLER,
    description: 'Horizontal divider strip',
    icon: 'minus-square',
    constraints: {
      minWidth: 12,
      maxWidth: 96,
      minHeight: 0.5,
      maxHeight: 6,
      minDepth: 12,
      maxDepth: 24,
    },
    defaultProps: {
      width: 24,
      height: DEFAULT_HORIZONTAL_FILLER_THICKNESS,
      depth: 24,
    },
  },
  
  // Legacy: Keep for backward compatibility with existing designs
  // Maps to Vertical Filler
  {
    id: 'filler',
    name: 'Vertical Filler',
    category: CATALOG_CATEGORIES.FILLER,
    description: 'Vertical gap filler panel (legacy)',
    icon: 'filler',
    constraints: {
      minWidth: 0.375,
      maxWidth: 6,
      minHeight: 12,
      maxHeight: 96,
      minDepth: 12,
      maxDepth: 24,
    },
    defaultProps: {
      width: 3,
      height: 84,
      depth: 24,
      count: 1,
    },
  },
  
  // Buffer - Reserved space at wall edges (corner clearance)
  {
    id: 'buffer',
    name: 'Buffer',
    category: CATALOG_CATEGORIES.BUFFER,
    description: 'Reserved corner/edge clearance space',
    icon: 'square-dashed',
    constraints: {
      minWidth: 2,
      maxWidth: 24,
      minHeight: 12,
      maxHeight: 96,
      minDepth: 1,
      maxDepth: 24,
    },
    defaultProps: {
      width: 6,
      height: 84,
      depth: 14,
      position: 'left', // 'left' or 'right' - which side of wall
    },
  },

  // Multi-Column Container — V1 nested layout block
  // Holds N columns of components side-by-side. Children carry
  // `parentContainerId` + `containerColumnIndex` so they live in
  // `project.items[]` flat array (zero-regression).
  {
    id: 'multi-column-container',
    name: 'Multi-Column',
    category: CATALOG_CATEGORIES.MULTI_COLUMN_CONTAINER,
    description: 'Side-by-side column block with custom widths',
    icon: 'columns',
    constraints: {
      minWidth: 24,        // sum of all column widths
      maxWidth: 144,
      minHeight: 12,
      maxHeight: 120,
      minDepth: 12,
      maxDepth: 24,
      minColumnCount: 2,
      maxColumnCount: 6,
      minColumnWidth: 8,
      maxColumnWidth: 60,
    },
    defaultProps: {
      width: 60,           // sum of columnWidths
      height: 84,
      depth: 24,
      columnCount: 2,
      columnWidths: [30, 30],
    },
  },

  // Hook Rack Panel — wall-mounted coat-hook accessory
  {
    id: 'hook-rack',
    name: 'Hook Rack',
    category: CATALOG_CATEGORIES.HOOK_RACK,
    description: 'Wall-mounted hook panel (coats, bags)',
    icon: 'hook',
    constraints: {
      minWidth: 8, maxWidth: 60,
      minHeight: 4, maxHeight: 96,
      minDepth: 1, maxDepth: 24,
      minCount: 2, maxCount: 8, // hook count
    },
    defaultProps: {
      width: 24,
      height: 48,
      depth: 14,
      count: 4,
    },
  },

  // TV / Media Niche — open recess (no back panel in 3D)
  {
    id: 'tv-niche',
    name: 'TV Niche',
    category: CATALOG_CATEGORIES.TV_NICHE,
    description: 'Open recess for TV / display',
    icon: 'tv',
    constraints: {
      minWidth: 18, maxWidth: 72,
      minHeight: 12, maxHeight: 48,
      minDepth: 6, maxDepth: 24,
    },
    defaultProps: {
      width: 36,
      height: 24,
      depth: 14,
    },
  },

  // Pull-out Pant Rack — slides out drawer-style
  {
    id: 'pant-rack',
    name: 'Pant Rack',
    category: CATALOG_CATEGORIES.PANT_RACK,
    description: 'Pull-out trouser rack with rods',
    icon: 'shirt',
    constraints: {
      minWidth: 18, maxWidth: 36,
      minHeight: 4, maxHeight: 60,
      minDepth: 14, maxDepth: 24,
      minCount: 4, maxCount: 12, // rod count
    },
    defaultProps: {
      width: 24,
      height: 6,
      depth: 18,
      count: 6,
    },
  },

  // Mirror Panel — tall vertical mirror (glass surface with thin frame)
  {
    id: 'mirror',
    name: 'Mirror',
    category: CATALOG_CATEGORIES.MIRROR,
    description: 'Tall vertical mirror panel with thin frame',
    icon: 'square',
    constraints: {
      minWidth: 8, maxWidth: 48,
      minHeight: 12, maxHeight: 96,
      minDepth: 1, maxDepth: 6,
    },
    defaultProps: {
      width: 16,
      height: 60,
      depth: 2,
    },
  },

  // Corner Component — 90-degree L-shaped corner cabinet spanning 2 walls.
  // Consumes `firstWallLength` at the end of `wallId` (primary wall) and
  // `secondWallLength` at the start of `secondaryWallId`. The `width`
  // field mirrors `firstWallLength` for standard rendering pipelines.
  // cornerPair: 'A_B' (Wall A + Wall B) | 'B_C' (Wall B + Wall C)
  // cornerMode: 'shelves' | 'hanging'
  // hangingSide: 'first' | 'second' (rod on primary or secondary side)
  {
    id: 'corner',
    name: 'Corner Cabinet',
    category: CATALOG_CATEGORIES.CORNER,
    description: 'L-shaped corner cabinet spanning two adjacent walls',
    icon: 'square',
    constraints: {
      minWidth: 12, maxWidth: 36,
      minHeight: 24, maxHeight: 96,
      minDepth: 12, maxDepth: 30,
    },
    defaultProps: {
      width: 24,           // === firstWallLength
      height: 84,
      depth: 24,
      firstWallLength: 24,
      secondWallLength: 24,
      cornerPair: 'A_B',
      cornerMode: 'shelves',
      shelfCount: 4,
      hangingSide: 'first',
      hasDoor: false,
      doorMode: 'double',
    },
  },
];

// Global Accessories - Style Settings (not placeable components)
export const WOOD_COLORS = {
  white: { name: 'White', color: '#ffffff' },
  cream: { name: 'Cream', color: '#fffdd0' },
  naturalOak: { name: 'Natural Oak', color: '#c9a961' },
  walnut: { name: 'Walnut', color: '#5c4033' },
  espresso: { name: 'Espresso', color: '#3c2415' },
  gray: { name: 'Gray', color: '#808080' },
  black: { name: 'Black', color: '#1a1a1a' },
};

export const HANDLE_STYLES = {
  bar: { name: 'Bar', description: 'Modern bar pull' },
  bow: { name: 'Bow', description: 'Curved bow handle' },
  crescent: { name: 'Crescent', description: 'Crescent moon shape' },
  knob: { name: 'Knob', description: 'Round knob' },
  none: { name: 'None', description: 'No hardware (push-to-open)' },
};

export const HARDWARE_FINISHES = {
  chrome: { name: 'Polished Chrome', color: '#d4d4d4' },
  nickel: { name: 'Brushed Nickel', color: '#c0c0c0' },
  bronze: { name: 'Oil-Rubbed Bronze', color: '#3d2b1f' },
  black: { name: 'Matte Black', color: '#1a1a1a' },
  brass: { name: 'Satin Brass', color: '#b5a642' },
};

export const FLOOR_FINISHES = {
  carpet: { name: 'Carpet', color: '#808080' },
  tile: { name: 'Tile', color: '#d3d3d3' },
  laminate: { name: 'Laminate', color: '#c4a35a' },
  vinyl: { name: 'Vinyl', color: '#9b8d7a' },
};

export const DOOR_TYPES = {
  none: { name: 'No Door', icon: 'square' },
  sliding: { name: 'Sliding', icon: 'move-horizontal' },
  single: { name: 'Single', icon: 'door-open' },
  double: { name: 'Double', icon: 'columns' },
  bifold: { name: 'Bi-Fold', icon: 'fold-vertical' },
};

// Get catalog item by ID
export const getCatalogItem = (id) => {
  return CATALOG.find(item => item.id === id);
};

// Get items by category
export const getCatalogByCategory = (category) => {
  return CATALOG.filter(item => item.category === category);
};

// Validate item against constraints
export const validateItemConstraints = (item, catalogItem) => {
  const c = catalogItem.constraints;
  const errors = [];
  
  if (item.width < c.minWidth) errors.push(`Width must be at least ${c.minWidth}"`);
  if (item.width > c.maxWidth) errors.push(`Width cannot exceed ${c.maxWidth}"`);
  if (item.height < c.minHeight) errors.push(`Height must be at least ${c.minHeight}"`);
  if (item.height > c.maxHeight) errors.push(`Height cannot exceed ${c.maxHeight}"`);
  if (item.depth < c.minDepth) errors.push(`Depth must be at least ${c.minDepth}"`);
  if (item.depth > c.maxDepth) errors.push(`Depth cannot exceed ${c.maxDepth}"`);
  
  return { valid: errors.length === 0, errors };
};

// Get count limits for a component type
export const getCountLimits = (categoryOrId) => {
  return { min: 1, max: 20 };
};

// Validate each level/drawer height
export const validateEachHeight = (eachHeight, count, totalHeight) => {
  if (eachHeight <= 0) {
    return { valid: false, error: 'Height must be greater than 0' };
  }
  const requiredHeight = count * eachHeight;
  if (requiredHeight > totalHeight) {
    return { valid: false, error: `Total required height (${count} × ${eachHeight}" = ${requiredHeight}") exceeds component height (${totalHeight}")` };
  }
  return { valid: true };
};

/**
 * Normalize component type to standard format
 * Handles variations like "Corner Shelf", "corner-shelf", "corner_shelf", "cornerShelf"
 * @param {string} type - The component type in any format
 * @returns {string} - Normalized type ID (e.g., "corner-shelf")
 */
export const normalizeComponentType = (type) => {
  if (!type) return null;
  
  // Convert to lowercase and replace underscores/spaces with hyphens
  const normalized = type.toLowerCase()
    .replace(/_/g, '-')
    .replace(/\s+/g, '-')
    .replace(/([a-z])([A-Z])/g, '$1-$2') // camelCase to kebab-case
    .toLowerCase();
  
  // Map common variations
  const typeMap = {
    'shelves': 'shelves',
    'shelf': 'shelves',
    'drawers': 'drawers',
    'drawer': 'drawers',
    'hanging': 'hanging',
    'shoe-shelves': 'shoe-shelves',
    'shoeshelves': 'shoe-shelves',
    'shoe-shelf': 'shoe-shelves',
    'vertical-filler': 'vertical-filler',
    'verticalfiller': 'vertical-filler',
    'filler': 'vertical-filler', // Legacy filler maps to vertical filler
    'horizontal-filler': 'horizontal-filler',
    'horizontalfiller': 'horizontal-filler',
    'bottom-filler': 'horizontal-filler', // Legacy bottom filler maps to horizontal filler
    'mirror': 'mirror',
    'mirror-panel': 'mirror',
    'wall-mirror': 'mirror',
    'corner': 'corner',
    'corner-cabinet': 'corner',
    'corner-shelf': 'corner',
    'corner-shelves': 'corner',
    'l-shape': 'corner',
    'l-shaped-corner': 'corner',
  };
  
  return typeMap[normalized] || normalized;
};

/**
 * Map category to settings key for available components
 */
export const categoryToSettingsKey = {
  [CATALOG_CATEGORIES.SHELVES]: 'shelves',
  [CATALOG_CATEGORIES.DRAWERS]: 'drawers',
  [CATALOG_CATEGORIES.HANGING]: 'hanging',
  [CATALOG_CATEGORIES.SHOE_SHELVES]: 'shoeShelves',
  [CATALOG_CATEGORIES.VERTICAL_FILLER]: 'verticalFiller',
  [CATALOG_CATEGORIES.HORIZONTAL_FILLER]: 'horizontalFiller',
  [CATALOG_CATEGORIES.BUFFER]: 'buffer',
  [CATALOG_CATEGORIES.MULTI_COLUMN_CONTAINER]: 'multiColumnContainer',
  [CATALOG_CATEGORIES.HOOK_RACK]: 'hookRack',
  [CATALOG_CATEGORIES.TV_NICHE]: 'tvNiche',
  [CATALOG_CATEGORIES.PANT_RACK]: 'pantRack',
  [CATALOG_CATEGORIES.MIRROR]: 'mirror',
  [CATALOG_CATEGORIES.CORNER]: 'corner',
  // Legacy mappings
  [CATALOG_CATEGORIES.FILLER]: 'verticalFiller',
  [CATALOG_CATEGORIES.BOTTOM_FILLER]: 'horizontalFiller',
};

